import React, { useEffect, useState } from 'react';
import { getAuthorNoteList, getAuthorNoteDetail, getCommentList } from '../../../api';
import Paging from '../../../common/layouts/page/Paging';
import { useIsMobile } from '../../../hooks/useIsMobile';

interface AuthorNote {
    noteId: string;
    category: string;
    title: string;
    content: string;
    thumbnail?: string;
    createdAt: string;
    likeCount?: number;
    commentCount?: number;
}

interface AuthorNoteListProps {
    authorEmail: string;
    isOwner: boolean;
    onViewDetail: (noteId: string) => void;
    onWrite: () => void;
    variant?: 'mypage' | 'authorDetail';
}

const CATEGORIES = ['전체', '영감 노트', '창작 노트', '작품 비하인드', '자유 글'];

const HeartIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
);

const CommentIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
);

const PenIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);

const NoImageIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </svg>
);

const getFirstImageSrc = (htmlContent: string): string | null => {
    if (!htmlContent) return null;
    const imgMatch = htmlContent.match(/<img[^>]+src="([^">]+)"/);
    return imgMatch ? imgMatch[1] : null;
};

const AuthorNoteList = ({ authorEmail, isOwner, onViewDetail, onWrite, variant = 'authorDetail' }: AuthorNoteListProps) => {
    const isMypage = variant === 'mypage';
    const isMobile = useIsMobile();
    const [notes, setNotes] = useState<AuthorNote[]>([]);
    const [category, setCategory] = useState('전체');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchNotes();
    }, [page, category, authorEmail]);

    const fetchNotes = async () => {
        if (!authorEmail) return;
        try {
            const categoryParam = category === '전체' ? undefined : category;
            const response = await getAuthorNoteList(page, authorEmail, categoryParam);

            let fetchedNotes: any[] = [];
            let total = 1;

            if (response?.page?.content) {
                fetchedNotes = response.page.content;
                total = response.page.totalPages || 1;
            } else if (response?.content) {
                fetchedNotes = response.content;
                total = response.totalPages || 1;
            } else if (Array.isArray(response)) {
                fetchedNotes = response;
            }

            // 목록 데이터만으로는 본문 전체가 오지 않으므로 상세 API를 각각 돌려 전체 HTML을 획득
            const detailPromises = fetchedNotes.map(async (item: any) => {
                const noteId = item.noteId || item.id;
                let fullContent = "";
                let adjustedCommentCount = item.commentCount || 0;

                try {
                    const detail = await getAuthorNoteDetail(noteId);
                    fullContent = detail.content || "";
                } catch (e) {
                    console.error("Failed to fetch detail for fallback thumbnail", e);
                }

                // 차단된 댓글 수 차감 보정
                try {
                    const commentData = await getCommentList(noteId, 0, 100);
                    if (commentData) {
                        const rawComments: any[] = commentData.content || commentData || [];
                        const blockedCount = rawComments.filter((c: any) => c.status === 'BLOCKED').length;
                        adjustedCommentCount = Math.max(0, (commentData.totalElements || rawComments.length || adjustedCommentCount) - blockedCount);
                    }
                } catch (e) {
                    console.error("Failed to fetch comments for count adjustment", e);
                }

                return { ...item, fullContent, adjustedCommentCount };
            });

            const notesWithDetails = await Promise.all(detailPromises);

            setNotes(notesWithDetails.map((item: any) => ({
                noteId: item.noteId || item.id,
                category: item.categoryName || item.category || '기타',
                title: item.title,
                content: item.fullContent || item.content || '',
                thumbnail: item.thumbnail,
                createdAt: item.createdAt,
                likeCount: item.likeCount || 0,
                commentCount: item.adjustedCommentCount,
            })));
            setTotalPages(total);
        } catch (error) {
            console.error("Failed to fetch notes", error);
        }
    };

    const handleCategoryClick = (cat: string) => {
        setCategory(cat);
        setPage(1);
    };

    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '');
        } catch {
            return dateStr;
        }
    };

    const stripHtml = (html: string) => {
        const plainText = html.replace(/<[^>]*>?/gm, '');
        return plainText.substring(0, Math.min(plainText.length, 200));
    };

    return (
        <div className={isMypage ? 'w-full px-4 py-6 md:px-[43px] md:py-[40px]' : 'w-full'}>

            {/* 모바일 마이페이지: 타이틀 + 글쓰기 버튼 (Figma 5878-6158) */}
            {isMypage && isMobile && (
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-[16px] font-semibold text-[#1f2937]">작가 노트</h2>
                    {isOwner && (
                        <button
                            onClick={onWrite}
                            className="flex items-center gap-1.5 h-[44px] px-4 bg-[#19cf7a] text-white rounded-full text-[14px] font-bold transition-colors hover:bg-[#00b368]"
                        >
                            <PenIcon />
                            글 쓰기
                        </button>
                    )}
                </div>
            )}

            {/* 카테고리 필터 (+ 데스크탑에서 글쓰기 버튼) */}
            <div className={`flex items-center justify-between flex-wrap gap-3 ${isMypage && isMobile ? 'mb-4' : 'mb-6'}`}>
                <div className="flex gap-2 flex-nowrap overflow-x-auto pb-1 scrollbar-none">
                    {CATEGORIES.map(cat => (
                        <button
                            key={cat}
                            onClick={() => handleCategoryClick(cat)}
                            className={`flex-shrink-0 px-4 rounded-full text-[14px] font-medium transition-colors whitespace-nowrap border ${
                                isMobile ? 'min-h-[44px]' : 'py-[7px]'
                            } ${
                                category === cat
                                    ? isMypage ? 'bg-[#19cf7a] border-[#19cf7a] text-white' : 'bg-[#10b981] border-[#10b981] text-white'
                                    : 'bg-white text-[#4b5563] border-[#d1d5db] hover:bg-gray-50'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* 글쓰기 버튼 — 데스크탑 또는 비마이페이지만 */}
                {isOwner && (!isMypage || !isMobile) && (
                    <button
                        onClick={onWrite}
                        className={`flex items-center gap-1.5 px-5 py-2 text-white rounded-full text-[14px] font-medium transition-colors flex-shrink-0 ${
                            isMypage
                                ? 'bg-[#19cf7a] hover:bg-[#00b368]'
                                : 'bg-[#10b981] hover:bg-[#0d9f6e]'
                        }`}
                    >
                        <PenIcon />
                        글쓰기
                    </button>
                )}
            </div>

            {/* 안내 문구 — 작가 상세 페이지에서만 노출 */}
            {!isOwner && (
                <div className="flex items-center gap-2 bg-[rgba(255,255,255,0.5)] border border-[rgba(16,185,129,0.1)] rounded-[8px] px-3 py-3 mb-4">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span className="text-[#6b7280] text-[14px]">각 카테고리 별 작가님의 특별한 이야기를 만나보세요.</span>
                </div>
            )}

            {/* 노트 목록 */}
            <div className={`min-h-[200px] ${isMypage ? '' : 'bg-white rounded-[8px] px-8 py-8 md:px-4 md:py-5'}`}>
                <div className="flex flex-col gap-3 md:gap-4">
                    {notes.length > 0 ? (
                        notes.map(note => (
                            <div
                                key={note.noteId}
                                onClick={() => onViewDetail(note.noteId)}
                                className={`flex cursor-pointer items-start gap-4 transition-all ${
                                    isMypage
                                        ? 'bg-white rounded-[8px] p-6 md:p-4 hover:bg-[#fafafa]'
                                        : 'bg-white rounded-[8px] p-6 md:p-4 hover:shadow-md hover:bg-[#fafafa]'
                                }`}
                            >
                                {/* 텍스트 영역 */}
                                <div className="flex-1 min-w-0 flex flex-col gap-2 self-stretch">
                                    {/* 카테고리 + 날짜 */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="px-2.5 py-0.5 text-[12px] font-medium rounded-full bg-[#f0fdf4] text-[#0d894f]">
                                            {note.category}
                                        </span>
                                        <span className={`text-[#6b7280] ${isMypage ? 'text-[14px]' : 'text-[12px]'}`}>
                                            {formatDate(note.createdAt)}
                                        </span>
                                    </div>

                                    {/* 제목 */}
                                    <h3 className={`font-semibold text-[#1f2937] line-clamp-1 ${
                                        isMypage
                                            ? 'text-[15px] leading-[24px] md:text-[20px] md:leading-[28px]'
                                            : 'text-[15px] md:text-[18px]'
                                    }`}>
                                        {note.title}
                                    </h3>

                                    {/* 미리보기 */}
                                    <p className="text-[#4b5563] leading-[20px] line-clamp-2 text-[13px] md:text-[14px]">
                                        {stripHtml(note.content)}
                                    </p>

                                    {/* 댓글 수만 표시 */}
                                    <div className="flex items-center gap-3 text-[#6b7280] text-[13px] md:text-[14px] mt-auto pt-1">
                                        <span className="flex items-center gap-1">
                                            <CommentIcon />
                                            {note.commentCount || 0}
                                        </span>
                                    </div>
                                </div>

                                {/* 썸네일 */}
                                {(() => {
                                    const firstImg = getFirstImageSrc(note.content);
                                    const displayThumbnail = note.thumbnail || firstImg;
                                    const thumbSize = isMypage
                                        ? (isMobile ? 'w-[80px] h-[80px]' : 'w-[128px] h-[128px]')
                                        : (isMobile ? 'w-[80px] h-[80px]' : 'w-[120px] h-[120px]');
                                    if (displayThumbnail) {
                                        return (
                                            <div className={`rounded-[8px] overflow-hidden flex-shrink-0 ${thumbSize}`}>
                                                <img
                                                    src={displayThumbnail.startsWith('data:') || displayThumbnail.startsWith('http') ? displayThumbnail : `data:image/jpeg;base64,${displayThumbnail}`}
                                                    alt={note.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <div className={`rounded-[8px] bg-[#f3f4f6] flex-shrink-0 flex items-center justify-center text-[#9ca3af] ${thumbSize}`}>
                                                <NoImageIcon />
                                            </div>
                                        );
                                    }
                                })()}
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 text-[#a1a1a1] gap-3">
                            <p className="text-[15px]">작성된 작가 노트가 없습니다.</p>
                            {isOwner && (
                                <button
                                    onClick={onWrite}
                                    className={`mt-2 px-5 py-2 text-white rounded-full text-[14px] font-semibold transition-colors ${isMypage ? 'bg-[#19cf7a] hover:bg-[#00b368]' : 'bg-[#10b981] hover:bg-[#0d9f6e]'}`}
                                >
                                    첫 노트 작성하기
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* 페이지네이션 */}
                {notes.length > 0 && (
                    <div className="mt-6">
                        <Paging totalPages={totalPages} currentPage={page} onPageChange={setPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthorNoteList;
